import type React from "react";
import {
  Clock,
  MapPin,
  FileCheck,
  Package,
  PackageCheck,
  Building2,
  Building,
  Landmark,
} from "lucide-react";

export interface JourneyStepDef {
  id: string;
  titleKey: string;
  descKey: string;
  icon: React.ElementType;
}

export type JourneyStepStatus = "completed" | "current" | "pending";

export interface JourneyStep extends JourneyStepDef {
  title: string;
  description: string;
  status: JourneyStepStatus;
}

export const JOURNEY_STEP_DEFS: JourneyStepDef[] = [
  { id: "submitted", titleKey: "track.journeySteps.submitted", descKey: "track.journeySteps.submittedDesc", icon: FileCheck },
  { id: "under_process", titleKey: "track.journeySteps.underProcess", descKey: "track.journeySteps.underProcessDesc", icon: Clock },
  { id: "ready_for_collection", titleKey: "track.journeySteps.readyForCollection", descKey: "track.journeySteps.readyForCollectionDesc", icon: MapPin },
  { id: "passport_dispatch", titleKey: "track.journeySteps.passportDispatch", descKey: "track.journeySteps.passportDispatchDesc", icon: Package },
  { id: "delivered", titleKey: "track.journeySteps.delivered", descKey: "track.journeySteps.deliveredDesc", icon: PackageCheck },
  { id: "returned_to_embassy", titleKey: "track.journeySteps.returnedToEmbassy", descKey: "track.journeySteps.returnedToEmbassyDesc", icon: Building2 },
  { id: "delivered_at_mission", titleKey: "track.journeySteps.deliveredAtMission", descKey: "track.journeySteps.deliveredAtMissionDesc", icon: Building },
  // National visa + country policy: from inscan-at-mission onward, portal shows passport sent to embassy (client copy)
  { id: "application_lifecycle_end_at_mission", titleKey: "track.journeySteps.applicationLifecycleEndAtMission", descKey: "track.journeySteps.applicationLifecycleEndAtMissionDesc", icon: Landmark },
];

export const API_STATUS_TO_STEP_ID: Record<string, string> = {
  APPLICATION_SUBMITTED: "submitted",
  UNDER_PROCESS: "under_process",
  READY_FOR_COLLECTION: "ready_for_collection",
  PASSPORT_READY_TO_DISPATCH: "passport_dispatch",
  DELIVERED: "delivered",
  DELIVERED_AT_MISSION: "delivered_at_mission",
  PASSPORT_RETURNED_TO_EMBASSY: "returned_to_embassy",
  APPLICATION_LIFECYCLE_END_AT_MISSION: "application_lifecycle_end_at_mission",
};

const DEFAULT_STEP_ID = "under_process";

export function apiStatusToStepId(apiStatus: string | null | undefined): string {
  if (!apiStatus) return DEFAULT_STEP_ID;
  return API_STATUS_TO_STEP_ID[apiStatus] ?? DEFAULT_STEP_ID;
}

export function buildJourneySteps(
  currentStepId: string,
  t: (key: string) => string,
): JourneyStep[] {
  const currentIdx = JOURNEY_STEP_DEFS.findIndex((s) => s.id === currentStepId);
  return JOURNEY_STEP_DEFS.map((step, idx) => {
    let status: JourneyStepStatus = "pending";
    if (idx < currentIdx) status = "completed";
    else if (idx === currentIdx) status = "current";
    return { ...step, title: t(step.titleKey), description: t(step.descKey), status };
  });
}

export function resolveStatusDisplay(
  apiStatus: string | null | undefined,
  t: (key: string) => string,
): { title: string; description: string; icon: React.ElementType } {
  const stepId = apiStatusToStepId(apiStatus);
  const def = JOURNEY_STEP_DEFS.find((s) => s.id === stepId);
  return {
    title: def ? t(def.titleKey) : (apiStatus ?? t("track.journeySteps.underProcess")),
    description: def ? t(def.descKey) : t("track.journeySteps.underProcessDesc"),
    icon: def?.icon ?? MapPin,
  };
}
